import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';

export interface Breadcrumb {
  label: string;
  to?: string;
}

interface PageContainerProps {
  title: string;
  subtitle?: string;
  breadcrumbs?: Breadcrumb[];
  action?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * Container padrão de página do app Nexos — alinhado ao padrão de
 * `UsersPage` (`p-6 space-y-6`, H1 `text-2xl font-bold text-gray-900`).
 *
 * Encapsula:
 *  - padding externo + espaçamento vertical (`p-6 space-y-6`);
 *  - breadcrumb opcional (links azuis + separador ChevronRight);
 *  - título `text-2xl font-bold` + subtítulo `text-sm text-gray-500`;
 *  - slot `action` alinhado à direita para o CTA principal (ex.: "Novo").
 */
export function PageContainer({
  title,
  subtitle,
  breadcrumbs,
  action,
  children,
}: PageContainerProps) {
  return (
    <div className="p-6 space-y-6">
      {breadcrumbs && breadcrumbs.length > 0 && (
        <nav aria-label="Breadcrumb" className="flex items-center gap-1 text-xs text-gray-500">
          {breadcrumbs.map((b, i) => {
            const isLast = i === breadcrumbs.length - 1;
            return (
              <React.Fragment key={i}>
                {b.to && !isLast ? (
                  <Link to={b.to} className="text-blue-600 hover:underline">
                    {b.label}
                  </Link>
                ) : (
                  <span className={isLast ? 'text-gray-700' : ''}>{b.label}</span>
                )}
                {!isLast && <ChevronRight className="h-3 w-3 text-gray-400" aria-hidden />}
              </React.Fragment>
            );
          })}
        </nav>
      )}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
          {subtitle && <p className="mt-1 text-sm text-gray-500">{subtitle}</p>}
        </div>
        {action && <div className="min-w-0">{action}</div>}
      </div>
      {children}
    </div>
  );
}
